import { Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { Preferences } from '@capacitor/preferences';
import { AuthService } from '../../auth/auth.service';
import { DeviceMeta } from '../models/point.model';
import { Observable, from } from 'rxjs';

// Configuration constants
const DEVICE_META_CONFIG = {
  URL_PATTERNS: {
    INTENNSE_OFFICIAL: '/intennse-official-view/',
    OFFICIAL_VIEW: '/official-view/',
    LIVE_SCORING: '/live-scoring/',
    TIE_ADVANCED_TRACKING: '/match/',
    TOURNAMENT_LIVE_SCORE: '/live-score',
    DEFAULT: 'match-list-view'
  }
} as const;

interface DeviceInfo {
  platform: string;
  model: string;
}

interface BatteryInfo {
  batteryLevel: number;
}

@Injectable({
  providedIn: 'root',
})
export class DeviceMetaService {
  constructor(private authService: AuthService) {}

  /**
   * Get device metadata (always fresh, no cache)
   */
  async getDeviceMeta(): Promise<DeviceMeta> {
    try {
      return await this.fetchDeviceMeta();
    } catch (error) {
      console.error('Failed to get device metadata:', error);
      throw new Error('Unable to retrieve device information');
    }
  }

  /**
   * Get device metadata as an observable (always fresh)
   */
  getDeviceMeta$(): Observable<DeviceMeta> {
    return from(this.getDeviceMeta());
  }

  /**
   * Fetch device metadata from device APIs
   */
  private async fetchDeviceMeta(): Promise<DeviceMeta> {
    const [deviceInfo, batteryInfo, deviceId] = await Promise.all([
      this.getDeviceInfo(),
      this.getBatteryInfo(),
      this.getDeviceId()
    ]);

    const url = window.location.href;
    const mode = this.getModeFromUrl(url);
    const userId = this.authService.authUserSignal()?._id ?? '';

    return {
      batteryLevel: Math.round((batteryInfo?.batteryLevel ?? 0) * 100),
      platform: deviceInfo.platform,
      model: deviceInfo.model,
      mode,
      deviceId: deviceId ?? '',
      trackerId: userId,
      timestamp: new Date(),
    };
  }

  /**
   * Get device information with error handling
   */
  private async getDeviceInfo(): Promise<DeviceInfo> {
    try {
      const deviceInfo = await Device.getInfo();
      return {
        platform: deviceInfo.platform || 'unknown',
        model: deviceInfo.model || 'unknown'
      };
    } catch (error) {
      console.warn('Failed to get device info:', error);
      return {
        platform: 'unknown',
        model: 'unknown'
      };
    }
  }

  /**
   * Get battery information with error handling
   */
  private async getBatteryInfo(): Promise<BatteryInfo | null> {
    try {
      const batteryInfo = await Device.getBatteryInfo();
      return {
        batteryLevel: batteryInfo.batteryLevel || 0
      };
    } catch (error) {
      console.warn('Failed to get battery info:', error);
      return null;
    }
  }

  /**
   * Get device ID from preferences
   */
  private async getDeviceId(): Promise<string | null> {
    try {
      const { value } = await Preferences.get({ 
        key: 'deviceId' 
      });
      return value;
    } catch (error) {
      console.warn('Failed to get device ID from preferences:', error);
      return null;
    }
  }

  /**
   * Determine mode from current URL
   */
  private getModeFromUrl(url: string): string {
    const patterns = DEVICE_META_CONFIG.URL_PATTERNS;
    if (url.includes(patterns.INTENNSE_OFFICIAL)) {
      return 'intennse-official-view';
    }
    if (url.includes(patterns.OFFICIAL_VIEW)) {
      return 'official-view';
    }
    if (url.includes(patterns.TIE_ADVANCED_TRACKING)){
      return 'tie-advanced-tracking-view';
    }
    if (url.includes(patterns.TOURNAMENT_LIVE_SCORE)){
      return 'tournament-live-score-view';
    }
    if (url.includes(patterns.LIVE_SCORING)){
      return 'tournament-advanced-tracking-view';
    }
    return patterns.DEFAULT;
  }

  /**
   * Get device metadata with fallback values
   */
  async getDeviceMetaWithFallback(): Promise<DeviceMeta> {
    try {
      return await this.getDeviceMeta();
    } catch (error) {
      console.warn('Using fallback device metadata:', error);
      return this.getFallbackDeviceMeta();
    }
  }

  /**
   * Get fallback device metadata when device APIs fail
   */
  private getFallbackDeviceMeta(): DeviceMeta {
    const userId = this.authService.authUserSignal()?._id ?? '';
    const url = window.location.href;
    const mode = this.getModeFromUrl(url);

    return {
      batteryLevel: 0,
      platform: 'web',
      model: 'browser',
      mode,
      deviceId: 'fallback-device',
      trackerId: userId,
      timestamp: new Date(),
    };
  }
} 